// US Federal Holiday Calculator
// Calculates dates for major US holidays

/**
 * Get the nth occurrence of a weekday in a month
 * @param {number} year - The year
 * @param {number} month - The month (0-11)
 * @param {number} weekday - The day of week (0=Sunday, 6=Saturday)
 * @param {number} occurrence - Which occurrence (1-5, or -1 for last)
 */
function getNthWeekdayOfMonth(year, month, weekday, occurrence) {
  if (occurrence === -1) {
    // Last occurrence - start from end of month and work backwards
    const date = new Date(year, month + 1, 0); // Last day of month
    while (date.getDay() !== weekday) {
      date.setDate(date.getDate() - 1);
    }
    return date.getDate();
  }

  // First occurrence - start from beginning of month
  const date = new Date(year, month, 1);
  while (date.getDay() !== weekday) {
    date.setDate(date.getDate() + 1);
  }

  // Add weeks to get to nth occurrence
  date.setDate(date.getDate() + (occurrence - 1) * 7);
  return date.getDate();
}

/**
 * Format date as YYYY-MM-DD
 */
function formatDate(year, month, day) {
  const m = String(month + 1).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  return `${year}-${m}-${d}`;
}

/**
 * Calculate US federal holidays for a given year
 * @param {number} year - The year to calculate holidays for
 * @returns {Array} Array of holiday objects with name, date, and reason
 */
export function calculateUSHolidays(year) {
  const holidays = [];

  // New Year's Day - January 1
  holidays.push({
    name: "New Year's Day",
    date: formatDate(year, 0, 1),
    reason: "New Year's Day"
  });

  // Martin Luther King Jr. Day - 3rd Monday in January
  holidays.push({
    name: "Martin Luther King Jr. Day",
    date: formatDate(year, 0, getNthWeekdayOfMonth(year, 0, 1, 3)),
    reason: "Martin Luther King Jr. Day"
  });

  // Presidents' Day - 3rd Monday in February
  holidays.push({
    name: "Presidents' Day",
    date: formatDate(year, 1, getNthWeekdayOfMonth(year, 1, 1, 3)),
    reason: "Presidents' Day"
  });

  // Memorial Day - Last Monday in May
  holidays.push({
    name: "Memorial Day",
    date: formatDate(year, 4, getNthWeekdayOfMonth(year, 4, 1, -1)),
    reason: "Memorial Day"
  });

  // Juneteenth - June 19
  holidays.push({
    name: "Juneteenth",
    date: formatDate(year, 5, 19),
    reason: "Juneteenth National Independence Day"
  });

  // Independence Day - July 4
  holidays.push({
    name: "Independence Day",
    date: formatDate(year, 6, 4),
    reason: "Independence Day"
  });

  // Labor Day - 1st Monday in September
  holidays.push({
    name: "Labor Day",
    date: formatDate(year, 8, getNthWeekdayOfMonth(year, 8, 1, 1)),
    reason: "Labor Day"
  });

  // Columbus Day - 2nd Monday in October
  holidays.push({
    name: "Columbus Day",
    date: formatDate(year, 9, getNthWeekdayOfMonth(year, 9, 1, 2)),
    reason: "Columbus Day"
  });

  // Veterans Day - November 11
  holidays.push({
    name: "Veterans Day",
    date: formatDate(year, 10, 11),
    reason: "Veterans Day"
  });

  // Thanksgiving - 4th Thursday in November
  holidays.push({
    name: "Thanksgiving",
    date: formatDate(year, 10, getNthWeekdayOfMonth(year, 10, 4, 4)),
    reason: "Thanksgiving Day"
  });

  // Christmas - December 25
  holidays.push({
    name: "Christmas Day",
    date: formatDate(year, 11, 25),
    reason: "Christmas Day"
  });

  return holidays;
}

/**
 * Get holidays for multiple years
 * @param {number} startYear - Starting year
 * @param {number} endYear - Ending year (inclusive)
 * @returns {Object} Object with years as keys and holiday arrays as values
 */
export function getHolidaysForYears(startYear, endYear) {
  const holidaysByYear = {};

  for (let year = startYear; year <= endYear; year++) {
    holidaysByYear[year] = calculateUSHolidays(year);
  }

  return holidaysByYear;
}

/**
 * Get a flat list of all holidays with year included
 * @param {number} startYear - Starting year
 * @param {number} endYear - Ending year (inclusive)
 * @returns {Array} Array of holiday objects with year, name, date, and reason
 */
export function getAllHolidays(startYear, endYear) {
  const allHolidays = [];

  for (let year = startYear; year <= endYear; year++) {
    const yearHolidays = calculateUSHolidays(year);
    yearHolidays.forEach(holiday => {
      allHolidays.push({
        ...holiday,
        year
      });
    });
  }

  return allHolidays;
}
